import { ServerConfig } from "@/config/config";
import { Job } from "@/models/job";
import { Contact, Product, ProductExtraFields, ProductPriceCategorySalesDates, ProductTrackingNumber, Vat } from "@/models/base";
import { ProductCompositionRow } from "@/models/base";
import { ProductImage } from "@/models/base";
import { Axios } from "@/utils/axios";
import { advancedSearch } from "@/utils/browse/browse";
import { IQueryParams } from "@/utils/query-params";
import { HttpError } from "@/utils/services/http-error";
import { LastOpenedItem } from "@/utils/last-opened";
import { View } from "@/models/view";
import { FetchedTotal } from "@/utils/views/fetched-total";
import { ProductAwsDocument } from "@/models/base/product-aws-document";
import { IGetResponse } from "@/utils/get-response";
import { AutoBarcode } from "@/models/dossier-configs/auto-barcode";
import { IExtraFieldQuickEditViaMenuDataResult } from "@/utils/models/extra-fields";
import { VatService } from "./vat-service";
import { UserConfigService } from "./user-config-service";
import { ProductBatchEditSellPricesBodyPrices } from "@/utils/products/product-batch-edit-sell-prices-body-prices";
import {ProductPriceRule} from "@/models/base/product-price-rule";
import { getModel } from "@/utils/models/model";
import { ExcelMapperService } from "@/utils/services/excel-mapper-service";

export enum ProductCSVImportExistingProductHandling {
	ERROR = "error",
	UPDATE = "update",
	IGNORE = "ignore"
}

export enum ProductCSVImportNewProductsHandling{
	CREATE = "create",
	IGNORE = "ignore"
}

export enum ProductCSVImportExistingBarCodeHandling {
	ERROR = "error",
	IGNORE = "ignore",
}

export enum ProductCSVImportProductMatchingMethod{
	SKU = "sku",
	SUPPLIER_CODE = "supplier-code",
	MANUFACTURER_CODE = "manufacturer-code",
	BARCODE = "bar-code"
}

export interface IGetProductByIdsOptions {
	preloads?:string[];
	fullPreloads?:boolean;
	batchSize?:number;
}



export class ProductServiceClass {
	selectedProducts = [] as Product[];

	lastOpenedItem = new LastOpenedItem("product", (product:Product)=>{
		product.ID = 0;
		product.Sku = "";
		product.ExtraFieldsID = 0;
		return product;
	});
	private readonly url = `${ServerConfig.host}/product`;
	private readonly compositionRowUrl = `${ServerConfig.host}/product-composition-row`;
	private readonly productTrackingUrl = `${ServerConfig.host}/product-tracking-number`

	async getProducts(query: IQueryParams): Promise<IGetResponse<Product>> {
		let result = await advancedSearch(query, this.url, getModel("Product"));
		let records = result.data.records as number;
		let contacts = result.data.data.map((c: any) => new Product(c));
		return {records, data: contacts};
	}

	async getTotals(view:View):Promise<FetchedTotal[]>{
		let result = await Axios.get(`${this.url}/view/${view.ID}/totals`);
		return (result.data || []).map((d:any)=>new FetchedTotal(d));
	}

	async getProduct(id: number): Promise<Product> {
		let result = await Axios.get(`${this.url}/${id}`, {});
		return new Product(result.data);
	}

	async getProductsForGroup(id:number):Promise<Product[]>{
		let result = await Axios.get(`${this.url}/by-group-id/${id}`, {});
		return result.data.map((p:any)=>new Product(p));
	}

	async getProductCount():Promise<Number>{
		let result = await Axios.get(`${this.url}`);
		return result.data.records;
	}

	async delProducts(products: Product[]): Promise<{DeletedCount:number,UnableToDeleteProducts:Product[]}> {
		let res = await Axios.delete(this.url, {
			data: products.map(p => p.ID)
		});
		return res.data;
	}

	async setProductsActive(products:Product[], active:boolean):Promise<void>{
		await Axios.post(`${this.url}/set-active`, {
			ProductIDs: products.map(p => p.ID),
			Active: active
		});
	}

	async getByBarCode(barCode:string, options:{excludedProductId?:number} = {}):Promise<Product>{
		let result = await Axios.get(`${this.url}/by-barcode/${encodeURIComponent(barCode)}`, {params: options});
		return new Product(result.data);
	}

	private async updateProductProcessImages(product:Product, newProduct:Product){
		let images = await this.uploadImages(newProduct, product.toUploadImages.map(t=>t.file));
		for (let index in product.toUploadImages){
			let img = product.toUploadImages[index];
			if (img.setAsCover) {
				await this.setCoverImage(newProduct, images[index]);
			}
		}

		for (let image of product.toRemoveImages) {
			await this.deleteImage(image);
		}
	}

	async postProduct(product: Product): Promise<Product> {
		let result = await Axios.post(`${this.url}`, product.getJSON());
		let p = new Product(result.data);
		if(product.UnprocessedFiles.length){
			for (let file of product.UnprocessedFiles){
				await this.uploadDocument(p,file);
			}
		}
		await this.updateProductProcessImages(product, p);
		return this.getProduct(p.ID);
	}

	async setCoverImage(product:Product, image:ProductImage) {
		await Axios.post(`${this.url}/${product.ID}/set-cover-image/${image.ID}`);
	}

	async putProduct(product: Product): Promise<Product> {
		let result = await Axios.put(`${this.url}`, product.getJSON());
		for (let file of product.UnprocessedFiles){
			await this.uploadDocument(product,file);
		}
		for (let file of product.toDeleteFiles){
			await this.deleteDocument(product, file);
		}
		let p = new Product(result.data);
		await this.updateProductProcessImages(product, p);
		return this.getProduct(p.ID);
	}

	async search(sku: string, includeDisabled:boolean = false): Promise<Product[]> {
		const encodedSku = encodeURIComponent(sku);
		const sortOrder = UserConfigService.getGeneralSettings().ProductSearchSortOrder || 0;
		let result = await Axios.get(`${this.url}/search?input=${encodedSku}&includeDisabled=${includeDisabled}&sortOrder=${sortOrder}`);
		return result.data.map((p: any) => new Product(p));
	}

	async existsBySku(sku: string, ): Promise<Boolean> {
		try{
			const encodedSku = encodeURIComponent(sku);
			await Axios.get(`${this.url}/by-barcode-or-sku?skuOrBarCode=${encodedSku}`);
			return true;
		}catch(err){
			let e = err as HttpError;
			if (e.status == 404){
				e.dontShow = true;
				return false;
			}
			throw e;
		}
	}
	async findBySku(sku: string): Promise<Product> {
		const encodedSku = encodeURIComponent(sku);
		let result = await Axios.get(`${this.url}/by-barcode-or-sku?skuOrBarCode=${encodedSku}`);
		return new Product(result.data);
	}

	async existsBySkuIncludeNonActive(sku: string): Promise<Boolean> {
		try{
			const encodedSku = encodeURIComponent(sku);
			await Axios.get(`${this.url}/by-barcode-or-sku-include-non-active?skuOrBarCode=${encodedSku}`);
			return true;
		}catch(err){
			let e = err as HttpError;
			if (e.status == 404){
				e.dontShow = true;
				return false;
			}
			throw e;
		}
	}

	async findBySkuMathcAfter0(sku:string):Promise<Product>{
		const encodedSku = encodeURIComponent(sku);
		let result = await Axios.get(`${this.url}/by-barcode-or-sku-match-after-0?skuOrBarCode=${encodedSku}`);
		return new Product(result.data);
	}

	async postProductCompositionRows(rows: ProductCompositionRow[]): Promise<ProductCompositionRow> {
		let result = await Axios.post(`${this.compositionRowUrl}`, rows.map(r => r.getJSON()));
		return result.data.map((r: any) => new ProductCompositionRow(r));
	}
	async putProductCompositionRows(rows: ProductCompositionRow[]): Promise<ProductCompositionRow> {
		let result = await Axios.put(`${this.compositionRowUrl}`, rows.map(r => r.getJSON()));
		return result.data.map((r: any) => new ProductCompositionRow(r));
	}
	async deleteProductCompositionRows(rows: ProductCompositionRow[]): Promise<void> {
		await Axios.delete(`${this.compositionRowUrl}`, {
			data: rows.map(r => r.ID)
		});
	}

	async uploadImages(product:Product, images:File[]):Promise<ProductImage[]> {
		if (images.length == 0) return [];
		let data = new FormData();
		data.append("image-count", images.length + "");
		for (let i in images) {
			let img = images[i];

			data.append(`img-${parseInt(i) + 1}`, img, img.name);
		}
		let result = await Axios.post(`${this.url}/${product.ID}/upload-images`, data);
		return result.data.map((r:any) => new ProductImage(r));
	}
	async deleteImage(image:ProductImage):Promise<void> {
		await Axios.delete(`${this.url}/image/${image.ID}`);
	}

	async importProductsCSV(file:File,
		existingProductHandling:ProductCSVImportExistingProductHandling,
		newProductsHandling:ProductCSVImportNewProductsHandling,
		productMatchingMethod:ProductCSVImportProductMatchingMethod,
		existingBarcodeHandling:ProductCSVImportExistingBarCodeHandling,
		useCommaSeperator:boolean):Promise<Job> {
		let data = new FormData();
		data.append("file", file, file.name);
		data.append("existing-product-handling", existingProductHandling);
		data.append("use-comma-as-decimal-seperator", useCommaSeperator ? "true" : "false");
		data.append("new-product-handling", newProductsHandling);
		data.append("product-matching-method", productMatchingMethod);
		data.append("existing-barcode-handling", existingBarcodeHandling);
		let result = await Axios.post(`${this.url}/import-csv`, data);
		return new Job(result.data);
	}

	async importProductsExcel(file:File,
		existingProductHandling:ProductCSVImportExistingProductHandling,
		newProductsHandling:ProductCSVImportNewProductsHandling,
		productMatchingMethod:ProductCSVImportProductMatchingMethod,
		existingBarcodeHandling:ProductCSVImportExistingBarCodeHandling):Promise<Job> {
		let data = new FormData();
		data.append("file", file, file.name);
		data.append("existing-product-handling", existingProductHandling);
		data.append("new-product-handling", newProductsHandling);
		data.append("product-matching-method", productMatchingMethod);
		data.append("existing-barcode-handling", existingBarcodeHandling);
		let result = await Axios.post(`${this.url}/import-excel`, data);
		return new Job(result.data);
	}

	public readonly excelMapperService = new ExcelMapperService(`${this.url}/excel-mapper-settings`)
	public async getImporterAllowedCols():Promise<string[]> {
		let result = await Axios.get(`${this.url}/importer-allowed-cols`);
		return result.data.map((i:string[])=>{
			return i.pop();
		});
	}

	public async uploadDocument(product:Product, file:File):Promise<ProductAwsDocument>{
		let data = new FormData();
		data.append("file", file, file.name);
		let result = await Axios.post(`${this.url}/${product.ID}/document`, data);
		return new ProductAwsDocument(result);
	}

	public async deleteDocument(product:Product, document:ProductAwsDocument):Promise<void>{
		await Axios.delete(`${this.url}/${product.ID}/document/${document.ID}`);
	}

	// returns list of affected products
	public async markForLabelPrinter(products:Product[]):Promise<number>{
		let ids = products.map(p=>p.ID);
		let result = await Axios.post(`${this.url}/mark-for-label-print`, ids);
		products.forEach(p=>p.MarkedToPrintLabel = true);
		return result.data.ProductsChanged;
	}

	// returns list of affected products
	public async unmarkForLabelPrinter(products:Product[]):Promise<number>{
		let ids = products.map(p=>p.ID);
		let result = await Axios.post(`${this.url}/unmark-for-label-print`, ids);
		products.forEach(p=>p.MarkedToPrintLabel = false);
		return result.data.ProductsChanged;
	}

	public async importFromKramp():Promise<Job>{
		let result = await Axios.post(`${this.url}/import-from-kramp`);
		return new Job(result.data);
	}

	public async getProductsByIds(ids:number[], options:IGetProductByIdsOptions = {}):Promise<Product[]>{
		let idLists = [...ids];
		let preloads = options.preloads || [];
		let fetchedProducts:Product[] = [];
		let batchSize = idLists.length;
		if (options.batchSize){
			batchSize = options.batchSize;
		}
		for (let i = 0; i < idLists.length; i+=batchSize){
			let toUseIds = idLists.splice(0, batchSize);
			let result = await Axios.get(`${this.url}/by-ids`, {params: {
				ids: toUseIds,
				preloads: encodeURIComponent(JSON.stringify(preloads)),
				fullPreloads: options.fullPreloads
			}});
			fetchedProducts.push(...(result.data as any[]).map(c=> new Product(c)));
		}
		return fetchedProducts;
	}

	public async batchEditSellPrices(products:Product[],dates:ProductPriceCategorySalesDates, OverwriteAllValues: boolean, prices:ProductBatchEditSellPricesBodyPrices[]){
		let ids = products.map(p=>p.ID);
		await Axios.post(`${this.url}/batch-set-prices`, {ProductIDs: ids,SaleDates: dates,OverwriteAllValues,Prices: prices});
	}

	public async getExtraFields(product:Product | number):Promise<ProductExtraFields>{
		if (typeof(product) == "object") {
			product = product.ID;
		}
		let result = await Axios.get(`${this.url}/${product}/extra-fields`);
		return new ProductExtraFields(result.data);
	}

	public async updateExtraFields(contact:Product, extraFields:ProductExtraFields):Promise<ProductExtraFields>{
		let result = await Axios.put(`${this.url}/extra-fields/${contact.ExtraFieldsID}`, extraFields.getJSON());
		return new ProductExtraFields(result.data);
	}

	async incrementAutoBarcode():Promise<AutoBarcode>{
		let result = await Axios.post(`${this.url}/increment-auto-barcode`);
		return new AutoBarcode(result.data);
	}

	async getProductsWithLessStockThenMinStockBySupplier(supplier:Contact, groupIds:number[]):Promise<Product[]>{
		let result = await Axios.get(`${this.url}/with-less-stock-then-min-stock-by-supplier/${supplier.ID}`, {params: {
			groupIds: JSON.stringify(groupIds)
		}});
		return ((result.data || []) as any[]).map(p=>new Product(p));
	}

	public async batchEditExtraField(products:Product[], field:string, value:IExtraFieldQuickEditViaMenuDataResult) {
		let body = {
			ExtraFieldIDs: products.map(s=>s.ExtraFieldsID),
			Field: field,
			NumValue: value.numValue,
			TextValue: value.textValue,
			BoolValue: value.boolValue,
			TimeValue: value.timeValue
		};
		await Axios.post(`${this.url}/batch-edit-extra-field`, body);
	}

	public async getProductsBySkus(skus:string[], options:IGetProductByIdsOptions = {}):Promise<Product[]>{
		let skusList = [...skus].filter((s, i)=>{
			return skus.indexOf(s) == i;
		});
		let preloads = options.preloads || [];
		let fetchedProducts:Product[] = [];
		let batchSize = skus.length;
		if (options.batchSize){
			batchSize = options.batchSize;
		}
		while (skusList.length > 0){
			let toUseSkus = skusList.splice(0, Math.min(batchSize, skusList.length));
			let result = await Axios.get(`${this.url}/by-skus`, {params: {
				skus: encodeURIComponent(JSON.stringify(toUseSkus)),
				preloads: encodeURIComponent(JSON.stringify(preloads)),
				fullPreloads: options.fullPreloads
			}});
			fetchedProducts.push(...(result.data as any[]).map(c=> new Product(c)));
		}
		return fetchedProducts;
	}

	public async getProductsByBarcodes(barcodes:string[], options:IGetProductByIdsOptions = {}):Promise<Product[]>{
		let barcodeList = [...barcodes].filter((s, i)=>{
			return barcodes.indexOf(s) == i;
		});
		let preloads = options.preloads || [];
		let fetchedProducts:Product[] = [];
		let batchSize = barcodes.length;
		if (options.batchSize){
			batchSize = options.batchSize;
		}
		while (barcodeList.length > 0){
			let toUseBarcodes = barcodeList.splice(0, Math.min(batchSize, barcodeList.length));
			let result = await Axios.get(`${this.url}/by-barcodes`, {params: {
				barcodes: encodeURIComponent(JSON.stringify(toUseBarcodes)),
				preloads: encodeURIComponent(JSON.stringify(preloads)),
				fullPreloads: options.fullPreloads
			}});
			fetchedProducts.push(...(result.data as any[]).map(c=> new Product(c)));
		}
		return fetchedProducts;
	}

	public async GetProductsBySkusOrBarCodes(codes:string[], options:IGetProductByIdsOptions = {}):Promise<Product[]>{
		let skusList = [...codes].filter((s, i)=>{
			return codes.indexOf(s) == i;
		});
		let preloads = options.preloads || [];
		let fetchedProducts:Product[] = [];
		let batchSize = codes.length;
		if (options.batchSize){
			batchSize = options.batchSize;
		}
		while (skusList.length > 0){
			let toUseCodes = skusList.splice(0, Math.min(batchSize, skusList.length));
			let result = await Axios.get(`${this.url}/by-skus-or-barcodes`, {params: {
				codes: encodeURIComponent(JSON.stringify(toUseCodes)),
				preloads: encodeURIComponent(JSON.stringify(preloads)),
				fullPreloads: options.fullPreloads
			}});
			fetchedProducts.push(...(result.data as any[]).map(c=> new Product(c)));
		}
		return fetchedProducts;
	}
	public async getProductBySku(sku:string):Promise<Product | null>{
		const encodedSku = encodeURIComponent(sku);
		let result = await Axios.get(`${this.url}/by-sku?sku=${encodedSku}`);
		return new Product(result.data);
	}

	public async generateRandomBarCode():Promise<string>{
		let result = await Axios.get(`${this.url}/generate-random-barcode`);
		return result.data;
	}

	public getDefaultNewProductVat():Vat {
		try{
			return VatService.getVat(UserConfigService.getSaleSettings().DefaultNewProductVatID);
		}catch(err){
			return VatService.getDefaultVat();
		}
	}

	public async setDefaultExtraFieldValues():Promise<void>{
		await Axios.post(`${this.url}/set-default-extra-field-values`);
	}

	public async addPriceRule(product:Product, rule:ProductPriceRule):Promise<ProductPriceRule>{
		let result = await Axios.post(`${this.url}/${product.ID}/price-rule`, rule.getJSON());
		rule = new ProductPriceRule(result.data);
		product.PriceRules.push(rule);
		return rule;
	}

	public async updatePriceRule(product:Product, rule:ProductPriceRule):Promise<ProductPriceRule>{
		let result = await Axios.put(`${this.url}/${product.ID}/price-rule/${rule.ID}`, rule.getJSON());
		rule = new ProductPriceRule(result.data);
		let i = product.PriceRules.findIndex(r=>r.ID == rule.ID);
		if (i == -1) {
			return rule;
		}
		product.PriceRules[i] = rule;
		return rule;
	}

	public async updateProductCompositions(id:number){
		await Axios.post(`${this.url}/${id}/update-referenced-compositions`);
	}

	public async removeToPrintLabels(productIds:number[], locationId:number):Promise<void>{
		await Axios.post(`${this.url}/remove-to-print-labels/${locationId}`, null, {params: {ids: encodeURIComponent(JSON.stringify(productIds))}});
	}

	public async setGroup(groupId:number | null, productIds:number[]):Promise<void>{
		let lists:number[][] = [];
		while(productIds.length > 0){
			lists.push([...productIds.splice(0, Math.min(10000, productIds.length))]);
		}
		for (let list of lists){
			await Axios.post(`${this.url}/set-group`, {ProductIDs: list, GroupID: groupId || null});
		}
	}

	public async setSizeTable(sizeTableId:number | null, productIds:number[]):Promise<void>{
		let lists:number[][] = [];
		while(productIds.length > 0){
			lists.push([...productIds.splice(0, Math.min(10000, productIds.length))]);
		}
		for (let list of lists){
			await Axios.post(`${this.url}/set-size-table`, {ProductIDs: list, SizeTableID: sizeTableId || null});
		}
	}

	public async setSimpleGroups(mainGroup:string, subGroup:string, productIds:number[]):Promise<void>{
		let lists:number[][] = [];
		while(productIds.length > 0){
			lists.push([...productIds.splice(0, Math.min(10000, productIds.length))]);
		}
		for (let list of lists){
			await Axios.post(`${this.url}/set-simple-group`, {ProductIDs: list,MainGroup: mainGroup,SubGroup: subGroup});
		}
	}

	public async resaveProducts(productIds:number[]):Promise<Job>{
		let result = await Axios.post(`${this.url}/resave-products`, productIds);
		return new Job(result.data);
	}

	public async setSellPrice(productId:number, category:number, price:number):Promise<Product>{
		let result = await Axios.post(`${this.url}/${productId}/set-sell-price`, {Category: category, Value: price});
		return new Product(result.data);
	}

	public async setSellPriceIncVat(productId:number, category:number, price:number):Promise<Product>{
		let result = await Axios.post(`${this.url}/${productId}/set-sell-price-inc-vat`, {Category: category, Value: price});
		return new Product(result.data);
	}

	public async setMainSupplierCatalogPrice(productId:number, price:number):Promise<Product>{
		let result = await Axios.post(`${this.url}/${productId}/set-main-supplier-catalog-price`, {Value: price});
		return new Product(result.data);
	}

	public async setMainSupplierNettoPrice(productId:number, price:number):Promise<Product>{
		let result = await Axios.post(`${this.url}/${productId}/set-main-supplier-netto-price`, {Value: price});
		return new Product(result.data);
	}

	public async getTrackingNumbersByDate(productId:number, limit:number, offset:number):Promise<ProductTrackingNumber[]>{
		let result = await Axios.get(`${this.productTrackingUrl}/${productId}/in-stock-by-validity-date`, {params: {limit, offset}});
		return (result.data || []).map((r:any)=>new ProductTrackingNumber(r));
	}

	public async searchSimpleMainGroup(input:string):Promise<string[]>{
		let result = await Axios.get(`${this.url}/search-simple-main-group/${encodeURIComponent(input)}`);
		return result.data || [];
	}

	public async searchSimpleSubGroup(mainGroup:string, input:string):Promise<string[]>{
		let result = await Axios.get(`${this.url}/search-simple-sub-group/${encodeURIComponent(mainGroup)}/${encodeURIComponent(input)}`);
		return result.data || [];
	}


};

export const ProductService = new ProductServiceClass();
